import type { LoaderFunctionArgs, MetaDescriptor } from '@remix-run/node';
import { redirect, useLoaderData } from '@remix-run/react';
import invariant from 'tiny-invariant';
import { getPageLocalisation } from '~/modules/seo/api/get-page-localisation.server';
import { createOrganisationSchema } from '~/modules/seo/schema/organisation-schema';
import { createWebsiteSchema } from '~/modules/seo/schema/website-schema';
import { createLDSchema } from '~/modules/seo/util/create-ld-schema';
import { getMetaBaseUrl } from '~/modules/seo/util/create-meta-base-url';
import {
    createMetaTitle,
    createMetaDescription,
    getCanonicalHreflang,
    createOGTags,
    mergeMeta,
} from '@modules/seo';
import { getRouteData } from '~/modules/root/util/get-route-data';
import { collectFallibleResources } from '@modules-rocco/root/api/collect-fallible-resources.server';
import { parseURL } from '@modules-rocco/url/api/parse-url';
import { mapCategoriesToTileViewModel } from '@modules-rocco/categories/data-mappers/category-to-tile';
import { getHomeContent } from '@modules-rocco/home/api/get-home-content.server';
import { getHomeHero } from '@modules-rocco/home/api/get-home-hero.server';
import { getProductCategories } from '@modules-rocco/products/api/get-product-categories.server';
import { getProfessionalCategories } from '@modules-rocco/professionals/api/get-professional-categories.server';
import { getActiveDirectoryCategoriesCount } from '~/modules-rocco/categories/api/get-active-directory-categories-count.server';
import { getFeaturedProjects as getRoccoFeaturedProjects } from '@modules-rocco/home/api/get-featured-projects';
import type { TileViewModel } from '@rocco/ui/tile-shared/view-models/tile';
import { getProjectsFromESSearchResults } from '~/modules-rocco/search/data-mappers/tile-project';
import type { ProjectTileViewModel } from '@rocco/ui/tile-project/view-models/project';
import { getProjectRootCategories } from '~/modules-rocco/projects/api/get-project-root-categories.server';
import { mapCategoriesToFilterTabsViewModel } from '~/modules-rocco/categories/data-mappers/category-to-filter-tabs';
import { getProjectCategories } from '~/modules-rocco/projects/api/get-project-categories.server';
import { mapCategoryToIslandOptions } from '~/modules-rocco/home/data-mapper/map-category-to-island-options';
import { getHomePins } from '~/modules/home/api/get-home-pins.server';
import { mapPinnedProjectsToTiles } from '~/modules-rocco/home/data-mapper/map-pinned-projects-to-tiles';

import { HomePageHero } from '@ui-rocco/pages/home-page/HomePageHero';
import { HomePageIntro } from '@ui-rocco/pages/home-page/HomePageIntro';
import { HomePageMedia } from '@ui-rocco/pages/home-page/HomePageMedia';
import { HomePageProjects } from '@ui-rocco/pages/home-page/HomePageProjects';
import { HomePageProducts } from '@ui-rocco/pages/home-page/HomePageProducts';
import { HomePageProfessionals } from '@ui-rocco/pages/home-page/HomePageProfessionals';
import { HomePageTestimonials } from '@ui-rocco/pages/home-page/HomePageTestimonials';
import { PageBottomSection } from '@ui-rocco/pages/shared/PageBottomSection';
import { HomePageIsland } from '@ui-rocco/pages/home-page/HomePageIsland';
import { HomePageFAQ } from '@ui-rocco/pages/home-page/HomePageFAQ';
import { useHomePageTracking } from '~/ui-rocco/hooks/use-home-page-tracking';
import { HomePageMarketingGuides } from '@ui-rocco/pages/home-page/HomePageMarketingGuides';
import { useOpenLogin } from '~/utils-rocco/hooks/open-login-modal';
import React from 'react';
import { useCurrentCategory } from '@rocco/ui/website-header/hooks/use-current-category';

export const meta = mergeMeta<typeof loader>(
    () => {
        return [];
    },
    (props) => {
        const { matches, data } = props;
        const title = createMetaTitle(
            props,
            [data?.localisation?.Title],
            false
        );
        const description = createMetaDescription([
            data?.localisation?.Description,
        ]);

        // Only extract rootData since we're not using appData
        const { rootData } = getRouteData(matches);

        const baseUrl = getMetaBaseUrl(props);
        invariant(
            rootData?.localisation.country,
            'index.ts meta(): country is required'
        );

        // ToDo: Video schema
        const organisationSchema = createOrganisationSchema(props);
        const websiteSchema = createWebsiteSchema(
            baseUrl,
            rootData.localisation.country
        );

        const ldSchema = createLDSchema([organisationSchema, websiteSchema]);

        // Temporarily remove preloading until structure is confirmed
        const preloadImageLinks: MetaDescriptor[] = [];

        return [
            { title },
            { name: 'description', content: description },
            ...createOGTags({
                meta: props,
                title,
                description,
            }),
            ...getCanonicalHreflang(props),
            ...ldSchema,
            ...preloadImageLinks,
        ];
    }
);

export const loader = async (args: LoaderFunctionArgs) => {
    const { context, request } = args;

    // Check user status first
    const { user } = context;
    const isLoggedIn = user.__typename === 'Me';

    // Redirect logged-in users to /app
    if (isLoggedIn) {
        return redirect('/app');
    }

    // Get localisation data
    const localisation = await getPageLocalisation(args);

    // Inline the logic from getNewHomePageloaderData
    const defaultFeaturedProjectCategoryLink = '/projects/residential';
    const url = new URL(request.url);

    const [
        hero,
        homeContent,
        productCategoriesResult,
        professionalCategoriesResult,
        activeDirectoryCategoriesCount,
        featuredProjectTilesResponse,
        projectRootCategories,
        projectCategoriesResult,
        pinnedItems,
    ] = await collectFallibleResources(
        [
            getHomeHero(context),
            getHomeContent(context),
            getProductCategories({ urlInfo: parseURL('/products') }, context),
            getProfessionalCategories(
                { urlInfo: parseURL('/professionals') },
                context
            ),
            getActiveDirectoryCategoriesCount({}, context),
            getRoccoFeaturedProjects(request, context, {
                categoryLink: defaultFeaturedProjectCategoryLink,
                limit: context.appDisplayMode === 'desktop' ? 8 : 4,
            }),
            getProjectRootCategories(context),
            getProjectCategories({ urlInfo: parseURL('/projects') }, context),
            getHomePins(context),
        ],
        context.logger
    );

    let featuredProjectTiles: TileViewModel<ProjectTileViewModel>[] = [];
    if (featuredProjectTilesResponse?.searchResults) {
        const featureProjects = getProjectsFromESSearchResults(
            featuredProjectTilesResponse?.searchResults
        );

        featuredProjectTiles = featureProjects.map((project) => ({
            aspectRatio: 'default',
            model: project,
        }));
    }

    const islandOptions = mapCategoryToIslandOptions({
        productCategories: productCategoriesResult?.Categories?.edges ?? [],
        professionalCategories:
            professionalCategoriesResult?.Categories?.edges ?? [],
        projectCategories:
            projectCategoriesResult?.Categories?.edges.sort(
                (a, b) => a.node.ID - b.node.ID
            ) ?? [],
    });

    const { intro, mediaSection } = homeContent ?? {};

    const { projects: pinnedProjects } = pinnedItems ?? {};

    const pinnedProjectsTiles: TileViewModel<ProjectTileViewModel>[] =
        pinnedProjects ? mapPinnedProjectsToTiles(pinnedProjects) : [];

    // Remove duplicate pinned projects and duplicate featured projects
    const allProjectTiles = [...pinnedProjectsTiles, ...featuredProjectTiles];
    const uniqueProjectTiles: TileViewModel<ProjectTileViewModel>[] = [];
    const seenProjectIds = new Set<string>();

    for (const tile of allProjectTiles) {
        if (!seenProjectIds.has(tile.model.id.toString())) {
            uniqueProjectTiles.push(tile);
            seenProjectIds.add(tile.model.id.toString());
        }
    }

    const homeProjectTiles = uniqueProjectTiles.slice(0, 8);

    // Build the roccoHomePageData directly
    const roccoHomePageData = {
        appDisplayMode: context.appDisplayMode,
        heroContent: {
            video: hero?.video,
            content: hero?.content,
        },
        introContent: {
            video: intro?.video,
            content: intro?.content ?? {
                title: '',
                description: '',
                listItems: [],
                CTAButton: {
                    text: '',
                    link: '',
                },
            },
        },
        testimonialSection: homeContent?.testimonialSection,
        HomeContentCampaign: homeContent?.homeContentCampaign,
        mediaSection: {
            title: mediaSection?.content?.title ?? '',
            description: mediaSection?.content?.description ?? '',
            CTAButton: mediaSection?.content?.CTAButton,
            video: mediaSection?.video,
        },
        productSection: {
            ...homeContent?.featuredProducts,
            categories: mapCategoriesToTileViewModel(
                productCategoriesResult?.Categories?.edges ?? []
            ),
            count:
                activeDirectoryCategoriesCount?.products.find(
                    (category) => category.title === 'Products'
                )?.count ?? 0,
        },
        professionalSection: {
            ...homeContent?.featuredProfessionals,
            categories: mapCategoriesToTileViewModel(
                professionalCategoriesResult?.Categories?.edges ?? []
            ),
            count:
                activeDirectoryCategoriesCount?.professionals.find(
                    (category) => category.title === 'Professionals'
                )?.count ?? 0,
        },
        projectSection: {
            ...homeContent?.featuredProjects,
            count:
                activeDirectoryCategoriesCount?.projects.find(
                    (category) => category.title === 'Projects'
                )?.count ?? 0,
            tiles: homeProjectTiles,
            filterTabs: mapCategoriesToFilterTabsViewModel(
                projectRootCategories?.Categories?.edges ?? [],
                new URL(`${url.origin}${defaultFeaturedProjectCategoryLink}`)
            ).sort((a, b) => a.id - b.id),
        },
        islandOptions,
        faqs: homeContent?.faqs ?? [],
    };

    // We only reach here if the user is logged out
    return {
        deviceType: context.device.type,
        localisation, // Keep localisation
        // Only return data relevant to the logged-out Rocco HomePage
        ...roccoHomePageData,
        isLoggedIn,
        pageTheme: 'transparent',
    };
};

const LoggedOutHomePage = () => {
    const { appDisplayMode, introContent, testimonialSection } =
        useLoaderData<typeof loader>();
    const isMobile = appDisplayMode === 'mobile';
    const { content, video } = introContent;
    const { title, description, listItems, CTAButton } = content;
    const openLogin = useOpenLogin();
    // initialize home page tracking
    useHomePageTracking();

    const { setCategory } = useCurrentCategory();

    // Set the current category for header navigation
    React.useEffect(() => {
        setCategory('All');
    }, [setCategory]);

    return (
        <>
            <HomePageHero />
            <HomePageIntro
                title={title}
                description={description}
                listItems={listItems}
                CTAButton={CTAButton}
                video={video ?? undefined}
                openLogin={openLogin}
            />
            <HomePageMedia openLogin={openLogin} />
            <HomePageMarketingGuides />
            <HomePageTestimonials
                testimonialSection={testimonialSection ?? undefined}
                appDisplayMode={appDisplayMode}
                openLogin={openLogin}
            />
            <HomePageProjects />
            <HomePageProducts />
            <HomePageProfessionals />
            <HomePageFAQ />
            <div className="my-10 flex flex-col gap-10 md:my-[12.8125rem] md:gap-[13.75rem]">
                <PageBottomSection openLogin={openLogin} />
            </div>

            {!isMobile ? <HomePageIsland /> : null}
        </>
    );
};

export default LoggedOutHomePage;
